import { AdminRegionResDto } from "service/api/estabrookSchemas";
import { create } from "zustand";
import { MapMarker } from "../components/map/map.types";
import { isUnassignedAddress } from "../shared/utils/address-assignment";

export type MoveValues = {
  region: string;
  sequence: number;
  order: string;
};

export interface RegionManagementStore {
  flatRate: boolean;
  onDemand: boolean;
  unassignedOrders: boolean;
  regions: AdminRegionResDto[];
  addresses: MapMarker[];
  allAddresses: MapMarker[]; // Unfiltered addresses for finding locations regardless of filter state
  selectedAddresses: MapMarker[];
  sequencedAddresses: MapMarker[];
  selectedAddressesForSequencing: MapMarker[];
  clickedAddress: MapMarker | null;
  mapCentering: MapMarker[];
  selectedRegionAddresses: string[];
  drawing: boolean;
  sidebar: boolean;
  mapZoom: number;
  unassignedAddresses: boolean;
  allSequenced: boolean;
  sameRegionId: boolean;
  validSequence: boolean;
  regionSequencing: string;
  lastSequence: number;
  remainingSequences: number;
  mapHeight: string;
  query: string;
  activeRegionShortDetails:
    | Pick<AdminRegionResDto, "color" | "name">
    | undefined;
  moveValues: MoveValues;
  setActiveRegionShortDetails: (
    data: Pick<AdminRegionResDto, "color" | "name">,
  ) => void;
  recalcEstStatus: "idle" | "started" | "success";
  setRecalcEstStatus: (status: "idle" | "started" | "success") => void;
  setRegions: (regions: AdminRegionResDto[]) => void;
  setSelectedAddressesForSequencing: (
    addresses: MapMarker[],
    targetSeqNumber?: number,
  ) => void;
  setQuery: (query: string) => void;
  setLastSequence: (seq: number) => void;
  setAddresses: (address: MapMarker[]) => void;
  setSequencedAddresses: (address: MapMarker[]) => void;
  setSelectedAddress: (address: MapMarker[], multiSelect?: boolean) => void;
  resetAllSelected: () => void;
  setDirectSelection: (addresses: MapMarker[]) => void;
  findAddress: (id: string) => MapMarker | undefined;
  setMapHeight: (val: string) => void;
  setRegionSequence: (id: string) => void;
  setFlatRate: (val: boolean) => void;
  findRegionAddresses: (val: string) => void;
  setRegionSequencing: (val: string) => void;
  setOnDemand: (val: boolean) => void;
  setUnassignedOrders: (val: boolean) => void;
  setSidebar: (show: boolean) => void;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  toggleDrawing: (drawing: boolean) => void;
  setMapCentering: (id: string | undefined, addresses?: MapMarker[]) => void;
  clearMapCentering: () => void;
  setMapZoom: (zoom: number) => void;
  removeSelected: (id: string) => void;
  setAddressSequence: (
    id: string,
    sequence?: number,
    unassigned?: boolean,
  ) => void;
  setMoveValues: (values: MoveValues) => void;
  activeTableRowIndex: number | null;
  setActiveTableRowIndex: (tableIndex: number) => void;
  pendingFocusRegionId: string | null;
  setPendingFocusRegionId: (regionId: string | null) => void;
  updateSelectedAddresses: (selectedAddresses: MapMarker[]) => void;
  setAllSequenced: (value: boolean) => void;
}

const useRegionManagementStore = create<RegionManagementStore>(
  (setState, getState) => ({
    addresses: [] as MapMarker[],
    allAddresses: [] as MapMarker[], // Initialize unfiltered addresses
    regions: [],
    sidebar: false,
    flatRate: false,
    onDemand: false,
    unassignedOrders: false,
    drawing: false,
    lastSequence: 1,
    allSequenced: false,
    regionSequencing: "",
    mapZoom: 13,
    clickedAddress: null,
    mapCentering: [] as MapMarker[],
    selectedAddresses: [] as MapMarker[],
    sequencedAddresses: [] as MapMarker[],
    selectedRegionAddresses: [],
    remainingSequences: 0,
    unassignedAddresses: true,
    validSequence: true,
    sameRegionId: true,
    query: "",
    loading: false,
    searchedAddresses: [] as MapMarker[],
    selectedRegionData: [],
    selectedAddressesForSequencing: [] as MapMarker[],
    moveValues: {
      region: "",
      order: "",
      sequence: 0,
    },
    mapHeight: "450px",
    activeTableRowIndex: null,
    pendingFocusRegionId: null,
    recalcEstStatus: "idle",
    setRecalcEstStatus: (status) => setState({ recalcEstStatus: status }),
    setMapHeight: (height) => setState({ mapHeight: height }),
    setSelectedAddressesForSequencing: (addresses, targetSeqNumber) =>
      setState(() => {
        const remaining = addresses.filter(
          (addr) => addr.locationSequence === null,
        ).length;

        return {
          selectedAddressesForSequencing: addresses,
          selectedAddresses: addresses.filter(
            (addr) => addr.locationSequence === null,
          ),
          sequencedAddresses: [],
          remainingSequences: remaining,
          lastSequence: targetSeqNumber || 1,
          allSequenced: false,
        };
      }),
    activeRegionShortDetails: undefined,
    setActiveRegionShortDetails: (data) =>
      setState({ activeRegionShortDetails: data }),
    setRegions: (regions) => {
      // Extract all addresses from regions to populate allAddresses for reliable findAddress
      const allAddressesFromRegions: Map<string, MapMarker> = new Map();

      for (const region of regions) {
        if (region.locations) {
          for (const location of region.locations) {
            // Only add if not already in the map (to avoid duplicates)
            if (!allAddressesFromRegions.has(location.locationId)) {
              allAddressesFromRegions.set(location.locationId, {
                id: location.locationId,
                locationSequence: location.locationSequence,
                region: {
                  id: region.id,
                  name: region.name,
                  color: region.color,
                },
                // These will be enriched from addresses when available
                address: "",
                latitude: 0,
                longitude: 0,
                selected: false,
              });
            }
          }
        }
      }

      setState((state) => ({
        regions,
        // Merge with existing allAddresses to preserve full address data if available
        allAddresses: Array.from(allAddressesFromRegions.values()).map(
          (marker) => {
            const existing = state.allAddresses.find((a) => a.id === marker.id);
            return existing && existing.address ? existing : marker;
          },
        ),
      }));
    },
    setSidebar: (show: boolean) => setState({ sidebar: show }),
    setAddresses: (data: MapMarker[]) => {
      setState((state) => {
        // Preserve selection state by checking if any new addresses are in the selectedAddresses
        const selectedIds = new Set(
          state.selectedAddresses.map((addr) => addr.id),
        );

        const addressesWithSelectionState = data.map((address) => ({
          ...address,
          selected: selectedIds.has(address.id),
        }));

        // Update selectedAddresses with fresh data while preserving selection
        const updatedSelectedAddresses = state.selectedAddresses.map(
          (selectedAddr) => {
            const freshData = data.find((addr) => addr.id === selectedAddr.id);
            if (freshData) {
              return {
                ...freshData,
                selected: true,
              };
            }
            return selectedAddr;
          },
        );

        // Merge new address data into allAddresses to enrich with full details
        const allAddressesMap = new Map(
          state.allAddresses.map((a) => [a.id, a]),
        );
        for (const address of addressesWithSelectionState) {
          allAddressesMap.set(address.id, address);
        }

        return {
          addresses: addressesWithSelectionState,
          allAddresses: Array.from(allAddressesMap.values()),
          selectedAddresses: updatedSelectedAddresses,
        };
      });
    },
    setRemainingSequence: (data: number) =>
      setState({ remainingSequences: data }),
    findAddress: (id: string): MapMarker | undefined => {
      const state = getState();

      // First try to find in the addresses array (filtered results)
      const foundInAddresses = state.addresses.find((item) => item.id === id);
      if (foundInAddresses) {
        return foundInAddresses;
      }

      // If not found in filtered addresses, try allAddresses
      const foundInAllAddresses = state.allAddresses.find(
        (item) => item.id === id,
      );
      return foundInAllAddresses;
    },
    setFlatRate: (val: boolean) =>
      setState(() => {
        return {
          flatRate: val,
        };
      }),
    setOnDemand: (val: boolean) =>
      setState(() => {
        return {
          onDemand: val,
        };
      }),
    setUnassignedOrders: (val: boolean) =>
      setState(() => {
        return {
          unassignedOrders: val,
        };
      }),
    setSelectedAddress: (selected, multiSelect = false) =>
      setState((state) => {
        const { addresses = [], selectedAddresses = [] } = state;

        let selectedArray: MapMarker[];

        if (multiSelect) {
          // Existing multi-select logic
          const newSelection = Array.isArray(selected) ? selected : [selected];
          selectedArray = newSelection.reduce((acc, item) => {
            const index = acc.findIndex((addr) => addr.id === item.id);
            if (index !== -1) {
              // If item is already selected, remove it
              return acc.filter((addr) => addr.id !== item.id);
            } else {
              // If item is not selected, add it
              return [...acc, item];
            }
          }, selectedAddresses);
        } else {
          // Single-select logic: only keep the clicked item
          const clickedItem = Array.isArray(selected) ? selected[0] : selected;
          const isAlreadySelected = selectedAddresses.some(
            (addr) => addr.id === clickedItem.id,
          );
          selectedArray = isAlreadySelected ? [] : [clickedItem];
        }

        const updatedAddresses = addresses.map((address) => ({
          ...address,
          selected: selectedArray.some(
            (selectedAddress) => selectedAddress.id === address.id,
          ),
        }));

        // New logic for selectedRegionAddresses
        const selectedRegionAddresses = selectedArray
          .map((item) => {
            const address = addresses.find((addr) => addr.id === item.id);
            return address ? address.id : "";
          })
          .filter(Boolean);

        const hasUnassignedAddresses = selectedArray.some(isUnassignedAddress);

        const areAllAssignedOrUnassigned =
          selectedArray.every((address) => address.region) ||
          selectedArray.every(isUnassignedAddress);

        const sameRegionId =
          areAllAssignedOrUnassigned &&
          !!selectedArray[0]?.region?.id && // Ensure this is a boolean
          selectedArray.every(
            (address) => address.region?.id === selectedArray[0].region?.id,
          );

        const sortedAddresses = selectedArray
          .filter((address) => address.locationSequence !== null)
          .sort((a, b) => a.locationSequence! - b.locationSequence!);

        const isValidSequence = sameRegionId
          ? sortedAddresses.every(
              (address, index, arr) =>
                index === 0 ||
                address.locationSequence ===
                  arr[index - 1].locationSequence! + 1,
            )
          : false;

        return {
          selectedAddresses: selectedArray,
          addresses: updatedAddresses,
          validSequence: isValidSequence,
          unassignedAddresses: hasUnassignedAddresses,
          sidebar: selectedArray.length > 0,
          sameRegionId,
          selectedRegionAddresses,
        };
      }),
    setDirectSelection: (addresses: MapMarker[]) =>
      setState((state) => {
        // Only create a new object for markers whose selected state actually
        // changed; preserve identity for everything else so unchanged markers
        // don't re-render (they should just restyle, never disappear/reappear).
        const selectedIdsSet = new Set(addresses.map((address) => address.id));
        const updatedAddresses = state.addresses.map((address) => {
          const shouldBeSelected = selectedIdsSet.has(address.id);
          if (address.selected !== shouldBeSelected) {
            return { ...address, selected: shouldBeSelected };
          }
          return address;
        });

        const allUnassignedAddresses = addresses.every(
          (address) => address.region === undefined || address.region === null,
        );

        const areAllAssignedOrUnassigned =
          addresses.every((address) => address.region) ||
          allUnassignedAddresses;

        const sameRegionId =
          areAllAssignedOrUnassigned &&
          !!addresses[0]?.region?.id &&
          addresses.every(
            (address) => address.region?.id === addresses[0].region?.id,
          );

        const sortedAddresses = addresses
          .filter((address) => address.locationSequence !== null)
          .sort((a, b) => a.locationSequence! - b.locationSequence!);

        const isValidSequence = sameRegionId
          ? sortedAddresses.every(
              (address, index, arr) =>
                index === 0 ||
                address.locationSequence ===
                  arr[index - 1].locationSequence! + 1,
            )
          : false;

        const selectedRegionAddresses = addresses
          .map((item) => {
            const address = state.addresses.find((addr) => addr.id === item.id);
            return address ? address.id : "";
          })
          .filter(Boolean);

        return {
          selectedAddresses: addresses,
          addresses: updatedAddresses,
          validSequence: isValidSequence,
          unassignedAddresses: allUnassignedAddresses,
          sidebar: addresses.length > 0,
          sameRegionId,
          selectedRegionAddresses,
        };
      }),
    resetAllSelected: () =>
      setState((state) => {
        const { addresses } = state;

        // Preserve identity for markers that are already unselected so they
        // don't re-render; only the previously-selected ones change.
        let addressesChanged = false;
        const allAddresses = addresses.map((address: MapMarker) => {
          if (!address.selected) return address;
          addressesChanged = true;
          return {
            ...address,
            selected: false,
          };
        });

        return {
          addresses: addressesChanged ? allAddresses : addresses,
          selectedAddresses: [],
          sequencedAddresses: [],
          sidebar: false,
          remainingSequences: 0,
          allSequenced: false,
          lastSequence: 1,
          regionSequencing: "",
          mapCentering: [],
        };
      }),
    removeSelected: (id: string) =>
      setState((state) => {
        const { selectedAddresses, addresses } = state;
        const updatedSelectedAddresses = selectedAddresses?.filter(
          (address) => address.id !== id,
        );

        if (updatedSelectedAddresses && updatedSelectedAddresses.length === 0) {
          setState({ sidebar: false });
        }

        const selectedIdsSet = new Set(
          updatedSelectedAddresses?.map(
            (selectedAddress) => selectedAddress.id,
          ) || [],
        );
        const updatedAddresses =
          addresses &&
          addresses.map((address: MapMarker) => {
            const shouldBeSelected = selectedIdsSet.has(address.id);
            if (address.selected !== shouldBeSelected) {
              return { ...address, selected: shouldBeSelected };
            }
            return address;
          });

        const allUnassignedAddresses = updatedSelectedAddresses.every(
          (address) => address.region === undefined || address.region === null,
        );

        const areAllAssignedOrUnassigned =
          updatedSelectedAddresses.every((address) => address.region) ||
          allUnassignedAddresses;

        const sameRegionId =
          areAllAssignedOrUnassigned &&
          !!updatedSelectedAddresses[0]?.region?.id && // Ensure this is a boolean
          updatedSelectedAddresses.every(
            (address) =>
              address.region?.id === updatedSelectedAddresses[0].region?.id,
          );

        const sortedAddresses = updatedSelectedAddresses
          .filter((address) => address.locationSequence !== null)
          .sort((a, b) => a.locationSequence! - b.locationSequence!);

        const isValidSequence = sameRegionId
          ? sortedAddresses.every(
              (address, index, arr) =>
                index === 0 ||
                address.locationSequence ===
                  arr[index - 1].locationSequence! + 1,
            )
          : false;

        return {
          selectedAddresses: updatedSelectedAddresses,
          addresses: updatedAddresses,
          validSequence: isValidSequence,
          unassignedAddresses: allUnassignedAddresses,
          sameRegionId,
        };
      }),
    setAllSequenced: (value?: boolean) =>
      setState((state) => {
        if (value !== undefined) {
          return { allSequenced: value };
        }
        const { addresses } = state;
        const allSequenced =
          addresses &&
          addresses
            .map((item) => item.locationSequence !== null)
            .every(Boolean);
        return { allSequenced };
      }),
    setRegionSequence: (id) =>
      setState((state) => {
        const { addresses } = state;
        const selectedRegionAddresses = addresses.filter(
          (item) => item.region?.id === id,
        );

        const clearedSequences = selectedRegionAddresses.map((item) => ({
          ...item,
          color: "unassigned",
          region: {
            ...item.region,
            color: "unassigned",
          },
          locationSequence: null,
        }));

        return {
          selectedAddresses: clearedSequences as MapMarker[],
          remainingSequences: clearedSequences.length,
        };
      }),
    findRegionAddresses: (id) =>
      setState((state) => {
        const { addresses } = state;
        const filteredAddresses = addresses.filter(
          (item) => item.region?.id === id,
        );

        const selectedRegionAddresses = filteredAddresses.map(
          (item) => item.id,
        );

        return {
          selectedRegionAddresses,
        };
      }),
    setRegionSequencing: (val) => setState({ regionSequencing: val }),
    toggleDrawing: (draw) => setState(() => ({ drawing: draw })),
    setMapCentering: (regionId, addresses) =>
      setState((state) => {
        let regionAddresses: MapMarker[] = [];
        if (addresses) {
          regionAddresses = addresses;
        }
        if (regionId) {
          regionAddresses = state.addresses.filter(
            (address) => address.region?.id === regionId,
          );
        }
        return {
          mapCentering: regionAddresses,
        };
      }),
    clearMapCentering: () => setState({ mapCentering: [] }),
    setMapZoom: (zoom) => setState(() => ({ mapZoom: zoom })),
    setSequencedAddresses: (data) => setState({ sequencedAddresses: data }),
    moveItem: (dragIndex, hoverIndex) =>
      setState((state) => {
        const {
          sequencedAddresses,
          selectedAddresses,
          selectedAddressesForSequencing,
        } = state;

        // Work with sequencedAddresses for both flows (same as create region)
        const items = [...sequencedAddresses];
        const [draggedItem] = items.splice(dragIndex, 1);
        items.splice(hoverIndex, 0, draggedItem);

        // Find the starting sequence number (preserve the original sequence range)
        const originalSequences = sequencedAddresses
          .map((item) => item.locationSequence)
          .filter((seq): seq is number => seq !== null && seq !== undefined)
          .sort((a, b) => a - b);

        const startingSequence =
          originalSequences.length > 0 ? originalSequences[0] : 1;

        // Update the locationSequence based on the new order, maintaining the original sequence range
        const updatedItems = items.map((item, index) => ({
          ...item,
          locationSequence: startingSequence + index,
        }));

        // Update selectedAddresses to reflect the new sequences
        const updatedSelectedAddresses = selectedAddresses?.map((address) => {
          const foundItem = updatedItems.find((item) => item.id === address.id);
          if (foundItem && address.locationSequence !== null) {
            return {
              ...address,
              locationSequence: foundItem.locationSequence,
            };
          }
          return address;
        });

        // Also update selectedAddressesForSequencing to reflect the new sequences (needed for move-to-existing-region flow)
        const updatedSelectedAddressesForSequencing =
          selectedAddressesForSequencing?.map((address) => {
            const foundItem = updatedItems.find(
              (item) => item.id === address.id,
            );
            if (foundItem && address.locationSequence !== null) {
              return {
                ...address,
                locationSequence: foundItem.locationSequence,
              };
            }
            return address;
          });

        // Check if all addresses are sequenced
        const allSequenced =
          updatedItems.length === selectedAddresses.length &&
          updatedItems.every((item) => item.locationSequence !== null);

        return {
          sequencedAddresses: updatedItems,
          selectedAddresses: updatedSelectedAddresses,
          selectedAddressesForSequencing: updatedSelectedAddressesForSequencing,
          allSequenced,
          remainingSequences: selectedAddresses.length - updatedItems.length,
        };
      }),
    setLastSequence: (seq) => setState(() => ({ lastSequence: seq })),
    setAddressSequence: (id, sequence, sequencingUnassigned) =>
      setState((state) => {
        const {
          selectedAddresses,
          selectedAddressesForSequencing,
          lastSequence,
        } = state;
        let assignedSequence: number = sequence || 0;
        if (sequence === undefined || sequence === 0) {
          assignedSequence = 1;
        }

        if (!selectedAddresses) return state;

        // For both flows, work with selectedAddresses to build sequencedAddresses
        const addressToRemove = selectedAddresses.find(
          (address) => address.id === id,
        );
        const sequenceToRemove =
          addressToRemove && addressToRemove.locationSequence;

        if (sequenceToRemove === undefined || sequenceToRemove === null) {
          // Adding sequence to an address
          const updatedSelectedAddresses = selectedAddresses.map((address) => {
            if (address.id === id) {
              return { ...address, locationSequence: assignedSequence };
            }
            return address;
          });

          const highestSequence = Math.max(
            0,
            ...updatedSelectedAddresses
              .map((address) => address.locationSequence)
              .filter(
                (seq): seq is number => seq !== undefined && seq !== null,
              ),
          );

          const nextSequence = highestSequence + 1;

          const allSequencedAddresses = updatedSelectedAddresses
            .filter((item) => item.locationSequence !== null)
            .sort(
              (a, b) =>
                (a.locationSequence as number) - (b.locationSequence as number),
            );

          // For sequencingUnassigned flow, also update selectedAddressesForSequencing
          let updatedSelectedAddressesForSequencing =
            selectedAddressesForSequencing;
          if (sequencingUnassigned) {
            updatedSelectedAddressesForSequencing =
              selectedAddressesForSequencing.map((address) => {
                if (address.id === id) {
                  return { ...address, locationSequence: assignedSequence };
                }
                return address;
              });
          }

          return {
            sequencedAddresses: allSequencedAddresses,
            selectedAddresses: updatedSelectedAddresses,
            selectedAddressesForSequencing:
              updatedSelectedAddressesForSequencing,
            remainingSequences:
              updatedSelectedAddresses.length - allSequencedAddresses.length,
            lastSequence: nextSequence > 1 ? nextSequence : 1,
            allSequenced:
              allSequencedAddresses.length === updatedSelectedAddresses.length,
          };
        }

        // Removing sequence from an address
        // Find the minimum sequence among all sequenced addresses to preserve the starting point
        const allSequencedBeforeRemoval = selectedAddresses
          .filter((addr) => addr.locationSequence !== null)
          .map((addr) => addr.locationSequence as number);
        const minSequence =
          allSequencedBeforeRemoval.length > 0
            ? Math.min(...allSequencedBeforeRemoval)
            : lastSequence;

        const updatedSelectedAddresses = selectedAddresses.map((address) => {
          if (address.id === id) {
            return { ...address, locationSequence: null };
          }
          if (
            address.locationSequence &&
            address.locationSequence > sequenceToRemove
          ) {
            return {
              ...address,
              locationSequence: address.locationSequence - 1,
            };
          }
          return address;
        });

        const filteredAddresses = updatedSelectedAddresses.filter(
          (address) => address.locationSequence !== null,
        );

        // Preserve the next sequence based on the highest current sequence or the original lastSequence
        const highestSequence =
          filteredAddresses.length > 0
            ? Math.max(
                0,
                ...filteredAddresses.map(
                  (address) => address.locationSequence as number,
                ),
              )
            : minSequence - 1;

        const nextSequence = highestSequence + 1;

        const allSequencedAddresses = updatedSelectedAddresses
          .filter((item) => item.locationSequence !== null)
          .sort(
            (a, b) =>
              (a.locationSequence as number) - (b.locationSequence as number),
          );

        // For sequencingUnassigned flow, also update selectedAddressesForSequencing
        let updatedSelectedAddressesForSequencing =
          selectedAddressesForSequencing;
        if (sequencingUnassigned) {
          updatedSelectedAddressesForSequencing =
            selectedAddressesForSequencing.map((address) => {
              if (address.id === id) {
                return { ...address, locationSequence: null };
              }
              if (
                address.locationSequence &&
                address.locationSequence > sequenceToRemove &&
                selectedAddresses.some(
                  (addressesToBeSequenced) =>
                    addressesToBeSequenced.id === address.id,
                )
              ) {
                return {
                  ...address,
                  locationSequence: address.locationSequence - 1,
                };
              }
              return address;
            });
        }

        return {
          sequencedAddresses: allSequencedAddresses,
          selectedAddresses: updatedSelectedAddresses,
          selectedAddressesForSequencing: updatedSelectedAddressesForSequencing,
          remainingSequences:
            updatedSelectedAddresses.length - allSequencedAddresses.length,
          lastSequence: nextSequence,
          allSequenced:
            allSequencedAddresses.length === updatedSelectedAddresses.length,
        };
      }),
    setQuery: (val) => setState({ query: val }),
    setMoveValues: (values) => setState({ moveValues: values }),
    setActiveTableRowIndex: (newValue) =>
      setState({ activeTableRowIndex: newValue }),
    setPendingFocusRegionId: (regionId) =>
      setState({ pendingFocusRegionId: regionId }),

    updateSelectedAddresses: (selectedAddresses) =>
      setState(() => ({ selectedAddresses })),
  }),
);

export default useRegionManagementStore;
